import { useState } from 'react'
import { format } from 'date-fns'
import {
  CalendarIcon,
  Plus,
  Edit,
  Trash2,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Clock,
  Loader2,
  AlertCircle,
} from 'lucide-react'
import { toast } from 'react-hot-toast'
import { cn, formatHours } from '@/lib/utils'
import {
  useGetVendorHoursQuery,
  useDeleteVendorHourMutation,
  type VendorHour,
} from '@/redux/service/vendor'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog'
import { Button } from './ui/button'
import { Calendar } from './ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table'
import { CustomPagination } from './CustomPagination'
import { Skeleton } from './ui/skeleton'
import { useCheckPermission } from '@/hooks/useCheckPermission'
import CreateUpdateVendorHours from './CreateUpdateVendorHours'

interface VendorHoursListProps {
  projectId: number
  vendorId: number
  vendorName: string
  vendorDefaultRate?: number
}

const VendorHoursList = ({
  projectId,
  vendorId,
  vendorName,
  vendorDefaultRate,
}: VendorHoursListProps) => {
  const [addEditOpen, setAddEditOpen] = useState(false)
  const [editingEntry, setEditingEntry] = useState<VendorHour | null>(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [entryToDelete, setEntryToDelete] = useState<VendorHour | null>(null)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [startDate, setStartDate] = useState<Date | undefined>()
  const [endDate, setEndDate] = useState<Date | undefined>()
  const [sortBy, setSortBy] = useState<string | undefined>()
  const [order, setOrder] = useState<'ASC' | 'DESC' | undefined>()
  const { hasPageAccess } = useCheckPermission()
  /* Adding/editing entries matches the backend's checkFinance gate (finance
     or mini_finance/Accounts); viewing rate & cost is finance-only, matching
     the stricter masking applied server-side in getVendorHours. */
  const canMutate = hasPageAccess('finance') || hasPageAccess('mini_finance')
  const canViewCost = hasPageAccess('finance')

  const [deleteVendorHour, { isLoading: isDeleting }] =
    useDeleteVendorHourMutation()

  const {
    data: vendorHoursData,
    isLoading,
    isFetching,
    isError,
  } = useGetVendorHoursQuery(
    {
      projectId,
      page,
      limit,
      ...(startDate ? { start_date: format(startDate, 'yyyy-MM-dd') } : {}),
      ...(endDate ? { end_date: format(endDate, 'yyyy-MM-dd') } : {}),
      ...(sortBy ? { sort_by: sortBy } : {}),
      ...(order ? { order } : {}),
    },
    { skip: !projectId }
  )

  const handleSort = (column: string) => {
    if (sortBy === column) {
      if (order === 'ASC') setOrder('DESC')
      else {
        setSortBy(undefined)
        setOrder(undefined)
      }
    } else {
      setSortBy(column)
      setOrder('ASC')
    }
    setPage(1)
  }

  const getSortIcon = (column: string) => {
    if (sortBy !== column)
      return <ArrowUpDown className='w-3 h-3 ml-1 opacity-40 inline-block' />
    if (order === 'ASC')
      return <ArrowUp className='w-3 h-3 ml-1 inline-block text-orange-500' />
    return <ArrowDown className='w-3 h-3 ml-1 inline-block text-orange-500' />
  }

  const fmtDate = (dateString: string | null | undefined) => {
    if (!dateString) return '—'
    return new Date(dateString).toLocaleDateString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  }

  const hasActiveFilters = startDate || endDate || sortBy
  const colCount = 4 + (canViewCost ? 2 : 0) + (canMutate ? 1 : 0)

  const rows: VendorHour[] =
    !isLoading && !isFetching ? (vendorHoursData?.data ?? []) : []

  const thClass =
    'text-xs font-semibold uppercase tracking-wide text-muted-foreground cursor-pointer select-none whitespace-nowrap px-4'

  return (
    <div className='space-y-4'>
      {/* ── Header ── */}
      <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-3'>
        <div className='flex items-center gap-3 flex-wrap'>
          <div className='flex items-center gap-2 px-3 py-1.5 rounded-lg bg-orange-500/10 border border-orange-500/20'>
            <Clock className='w-4 h-4 text-orange-500' />
            <span className='text-sm font-semibold text-orange-500'>
              VENDOR HOURS
            </span>
          </div>
          <span className='text-xs text-muted-foreground font-medium'>
            {vendorName}
          </span>
          {canMutate && (
            <Button
              size='sm'
              onClick={() => {
                setEditingEntry(null)
                setAddEditOpen(true)
              }}
              className='h-7 px-2.5 text-xs bg-orange-500 hover:bg-orange-600 text-white'
            >
              <Plus className='w-3.5 h-3.5 mr-1' /> Add
            </Button>
          )}
        </div>

        {/* ── Date filters ── */}
        <div className='flex flex-wrap items-center gap-2'>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant='outline'
                size='sm'
                className={cn(
                  'h-8 w-[130px] justify-start text-left font-normal text-xs border-border hover:bg-accent/30 transition-colors rounded-lg',
                  !startDate && 'text-muted-foreground'
                )}
              >
                <CalendarIcon className='mr-1.5 h-3 w-3 shrink-0' />
                {startDate ? format(startDate, 'PP') : 'Start Date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className='w-auto p-0' align='end'>
              <Calendar
                mode='single'
                captionLayout='dropdown'
                fromYear={2018}
                selected={startDate}
                onSelect={date => {
                  setStartDate(date)
                  setPage(1)
                }}
              />
            </PopoverContent>
          </Popover>

          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant='outline'
                size='sm'
                className={cn(
                  'h-8 w-[130px] justify-start text-left font-normal text-xs border-border hover:bg-accent/30 transition-colors rounded-lg',
                  !endDate && 'text-muted-foreground'
                )}
              >
                <CalendarIcon className='mr-1.5 h-3 w-3 shrink-0' />
                {endDate ? format(endDate, 'PP') : 'End Date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className='w-auto p-0' align='end'>
              <Calendar
                mode='single'
                captionLayout='dropdown'
                fromYear={2018}
                selected={endDate}
                onSelect={date => {
                  setEndDate(date)
                  setPage(1)
                }}
                initialFocus
                disabled={date => (startDate ? date < startDate : false)}
              />
            </PopoverContent>
          </Popover>

          {hasActiveFilters && (
            <Button
              variant='ghost'
              size='sm'
              className='h-8 px-2 text-xs text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors flex items-center gap-1'
              onClick={() => {
                setStartDate(undefined)
                setEndDate(undefined)
                setSortBy(undefined)
                setOrder(undefined)
                setPage(1)
              }}
            >
              <X className='h-3 w-3' />
              Reset
            </Button>
          )}
        </div>
      </div>

      {/* ── Table ── */}
      <div className='border border-border rounded-sm bg-card shadow-sm overflow-hidden'>
        <div className='overflow-x-auto'>
          <Table className='border-none'>
            <TableHeader>
              <TableRow className='border-b border-border bg-muted/40 hover:bg-muted/40'>
                <TableHead
                  className={thClass}
                  onClick={() => handleSort('date')}
                >
                  <div className='flex items-center gap-0.5'>
                    Date {getSortIcon('date')}
                  </div>
                </TableHead>
                <TableHead
                  className={thClass}
                  onClick={() => handleSort('hours')}
                >
                  <div className='flex items-center gap-0.5'>
                    Hours {getSortIcon('hours')}
                  </div>
                </TableHead>
                <TableHead className='text-xs font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap px-4'>
                  Description
                </TableHead>
                {canViewCost && (
                  <>
                    <TableHead
                      className={thClass}
                      onClick={() => handleSort('rate')}
                    >
                      <div className='flex items-center gap-0.5'>
                        Rate ($/hr) {getSortIcon('rate')}
                      </div>
                    </TableHead>
                    <TableHead className='text-xs font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap px-4'>
                      Total Cost
                    </TableHead>
                  </>
                )}
                <TableHead
                  className={thClass}
                  onClick={() => handleSort('created_at')}
                >
                  <div className='flex items-center gap-0.5'>
                    Created {getSortIcon('created_at')}
                  </div>
                </TableHead>
                {canMutate && (
                  <TableHead className='text-xs font-semibold uppercase tracking-wide text-muted-foreground text-right whitespace-nowrap px-4'>
                    Actions
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>

            <TableBody>
              {isLoading || isFetching ? (
                [...Array(limit)].map((_, i) => (
                  <TableRow key={i} className='border-b border-border/50'>
                    {[...Array(colCount)].map((_, j) => (
                      <TableCell key={j} className='px-4 py-3'>
                        <Skeleton className='h-4 w-full rounded-md' />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : rows.length > 0 ? (
                rows.map(entry => (
                  <TableRow
                    key={entry.id}
                    className='border-b border-border/50 hover:bg-accent/30 transition-colors duration-150 group'
                  >
                    <TableCell className='px-4 py-3 text-sm whitespace-nowrap'>
                      {fmtDate(entry.date)}
                    </TableCell>
                    <TableCell className='px-4 py-3 text-sm font-medium'>
                      {formatHours(entry.hours)}
                    </TableCell>
                    <TableCell
                      className='px-4 py-3 max-w-[280px] truncate text-sm text-muted-foreground'
                      title={entry.description ?? ''}
                    >
                      {entry.description ?? '—'}
                    </TableCell>
                    {canViewCost && (
                      <>
                        <TableCell className='px-4 py-3 text-sm font-medium'>
                          {entry.rate != null
                            ? `$${Number(entry.rate).toFixed(2)}`
                            : '—'}
                        </TableCell>
                        <TableCell className='px-4 py-3'>
                          <span className='inline-flex items-center gap-1 text-sm font-semibold text-orange-600 dark:text-orange-400'>
                            $
                            {entry.total_cost != null
                              ? Number(entry.total_cost).toFixed(2)
                              : entry.rate != null && entry.hours != null
                                ? (
                                    Number(entry.rate) * Number(entry.hours)
                                  ).toFixed(2)
                                : '—'}
                          </span>
                        </TableCell>
                      </>
                    )}
                    <TableCell className='px-4 py-3 text-xs text-muted-foreground whitespace-nowrap'>
                      {fmtDate(entry.created_at)}
                    </TableCell>
                    {canMutate && (
                      <TableCell className='sticky right-0 z-10 border-s bg-background/95 backdrop-blur-sm flex items-center gap-2 shadow-[-8px_0_12px_-12px_rgba(0,0,0,0.35)] opacity-0 group-hover:opacity-100 transition-opacity duration-300'>
                        <Button
                          size='icon'
                          variant='ghost'
                          onClick={() => {
                            setEditingEntry(entry)
                            setAddEditOpen(true)
                          }}
                          className='h-5 w-5 hover:bg-accent hover:text-accent-foreground'
                        >
                          <Edit />
                        </Button>
                        <Button
                          size='icon'
                          variant='ghost'
                          onClick={() => {
                            setEntryToDelete(entry)
                            setDeleteConfirmOpen(true)
                          }}
                          className='h-5 w-5 text-destructive/70 hover:text-destructive hover:bg-destructive/10'
                        >
                          <Trash2 />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : isError ? (
                <TableRow>
                  <TableCell colSpan={colCount} className='h-28 text-center'>
                    <div className='flex flex-col items-center justify-center gap-1.5'>
                      <AlertCircle className='w-7 h-7 text-destructive/50' />
                      <p className='text-sm font-medium text-muted-foreground'>
                        Failed to load vendor hours
                      </p>
                      <p className='text-xs text-muted-foreground/60'>
                        Something went wrong. Try refreshing.
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell colSpan={colCount} className='h-28 text-center'>
                    <div className='flex flex-col items-center justify-center gap-1.5'>
                      <Clock className='w-7 h-7 text-muted-foreground/40' />
                      <p className='text-sm font-medium text-muted-foreground'>
                        No vendor hours logged
                      </p>
                      <p className='text-xs text-muted-foreground/60'>
                        Use &quot;Add&quot; to log hours for this vendor
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* ── Pagination ── */}
      {!isError && vendorHoursData?.data && vendorHoursData.data.length > 1 && (
        <div className='pt-1'>
          <CustomPagination
            page={page}
            limit={limit}
            setPage={setPage}
            setLimit={setLimit}
            totalPages={vendorHoursData?.pagination?.totalPages}
            totalRecords={vendorHoursData?.pagination?.totalRecords}
          />
        </div>
      )}

      {/* ── Add / Edit dialog ── */}
      <CreateUpdateVendorHours
        open={addEditOpen}
        onClose={() => {
          setAddEditOpen(false)
          setEditingEntry(null)
        }}
        projectId={projectId}
        vendorId={vendorId}
        vendorName={vendorName}
        {...(vendorDefaultRate !== undefined ? { vendorDefaultRate } : {})}
        entry={editingEntry}
      />

      {/* ── Delete confirmation ── */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className='max-w-sm p-0 gap-0'>
          <DialogHeader className='px-6 pt-6 pb-4 border-b border-border'>
            <div className='flex items-center gap-3'>
              <div className='flex items-center justify-center w-8 h-8 rounded-lg bg-destructive/10 border border-destructive/20 shrink-0'>
                <Trash2 className='w-4 h-4 text-destructive' />
              </div>
              <DialogTitle className='text-base font-semibold'>
                Delete Vendor Hours
              </DialogTitle>
            </div>
          </DialogHeader>
          <div className='px-6 py-5'>
            <p className='text-sm text-muted-foreground'>
              Are you sure you want to delete these vendor hours? This action
              cannot be undone.
            </p>
          </div>
          <div className='flex justify-end gap-2 px-6 pb-5'>
            <Button
              variant='outline'
              size='sm'
              className='h-8 text-xs border-border hover:bg-accent/30'
              onClick={() => setDeleteConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant='destructive'
              size='sm'
              className='h-8 text-xs'
              disabled={isDeleting}
              onClick={async () => {
                if (entryToDelete?.id) {
                  try {
                    await deleteVendorHour({
                      projectId,
                      id: entryToDelete.id,
                    }).unwrap()
                    toast.success('Vendor hours deleted')
                    setDeleteConfirmOpen(false)
                    setEntryToDelete(null)
                  } catch {
                    toast.error('Failed to delete vendor hours')
                  }
                }
              }}
            >
              {isDeleting ? (
                <>
                  <Loader2 className='w-3 h-3 mr-1.5 animate-spin' />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default VendorHoursList
